import { Center, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconConfetti, IconSend } from "@tabler/icons-react";
import { Button, spacing, StatusBadge } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { PUBLISH_BLOCKER_KEY, type PublishBlocker } from "./publishBlocker";

interface AllDoneStateProps {
  approved: number;
  rejected: number;
  failed: number;
  canPublish: boolean;
  /** Why Publish is locked for the run, or null when it is allowed. */
  publishBlocker: PublishBlocker | null;
  onPublish: () => void;
}

/**
 * The centered "no more images to review" state (the reviewer-app's
 * PartyPopper screen), shown when nothing in the active run is pending. It
 * reports the approved / rejected counts and, for a `generation.admin` user,
 * surfaces the Publish call to action. When the run has nothing reviewable
 * (zero images, or every image failed) it says so instead of "every image
 * decided", and Publish stays available: failed images are returned as failures
 * (D20 d/e). When any image failed, a warning line sits next to Publish.
 */
export function AllDoneState({
  approved,
  rejected,
  failed,
  canPublish,
  publishBlocker,
  onPublish,
}: AllDoneStateProps) {
  const { t } = useTranslation();

  const publishDisabled = publishBlocker !== null;
  const tooltip = publishBlocker ? t(PUBLISH_BLOCKER_KEY[publishBlocker]) : t("review.publish.ready");
  const nothingReviewable = approved + rejected === 0;

  return (
    <Center style={{ flex: 1 }} p={spacing.xl}>
      <Stack align="center" gap={spacing.md} style={{ maxWidth: 480, textAlign: "center" }}>
        <IconConfetti size={48} aria-hidden="true" />
        <Text fw={700} size="xl">
          {nothingReviewable ? t("review.allDone.emptyTitle") : t("review.allDone.title")}
        </Text>
        <Text c="dimmed" size="sm">
          {nothingReviewable
            ? t("review.allDone.emptyDescription")
            : t("review.allDone.description")}
        </Text>
        {!nothingReviewable && (
          <Group gap={spacing.sm}>
            <StatusBadge tone="success">
              {t("review.allDone.approved", { count: approved })}
            </StatusBadge>
            <StatusBadge tone="error">
              {t("review.allDone.rejected", { count: rejected })}
            </StatusBadge>
          </Group>
        )}

        {failed > 0 && (
          <Text size="sm" c="dimmed">
            {t("review.publish.failedWarning", { count: failed })}
          </Text>
        )}

        {publishBlocker === "returning" ? (
          <StatusBadge tone="processing">{t("review.publish.returningTitle")}</StatusBadge>
        ) : (
          canPublish && (
            <Tooltip label={tooltip} withArrow>
              <span>
                <Button
                  leftSection={<IconSend size={16} />}
                  disabled={publishDisabled}
                  onClick={onPublish}
                >
                  {t("review.publish.button")}
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Stack>
    </Center>
  );
}
